import React, { useState, useEffect, useLayoutEffect, useRef, createContext, useContext } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
import "@/App.css";
import { translations } from "./translations";

// Logo URLs for each language and theme
const logos = {
  dark: {
    de: "/assets/logos/logo_de_new.png",
    en: "/assets/logos/logo_en_new.png",
    es: "/assets/logos/logo_es_new.png"
  },
  light: {
    de: "/assets/logos/logo_de_light.png",
    en: "/assets/logos/logo_en_light.png",
    es: "/assets/logos/logo_es_light.png"
  }
};

// WhatsApp number
const WHATSAPP_NUMBER = "491602838886";

// Theme Context
const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const theme = 'light';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    localStorage.removeItem('theme');
  }, []);

  return (
    <ThemeContext.Provider value={{ theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Language Context
const LanguageContext = createContext();
export const useLanguage = () => useContext(LanguageContext);

// Detect language based on browser/country
const detectLanguage = () => {
  // Check if user already has a saved preference
  const saved = localStorage.getItem('language');
  if (saved) return saved;
  
  // Get browser language
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  const langCode = browserLang.toLowerCase();
  
  // German-speaking: de, de-DE, de-AT, de-CH
  if (langCode.startsWith('de')) {
    return 'de';
  }
  
  // Spanish-speaking: es, es-ES, es-MX, es-AR, etc.
  if (langCode.startsWith('es')) {
    return 'es';
  }
  
  // All other languages default to English
  return 'en';
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(detectLanguage);
  const { theme } = useTheme();

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = translations[language];
  const logo = logos[theme][language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, logo }}>
      {children}
    </LanguageContext.Provider>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const { language } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);

  const messages = {
    de: "Hallo! Ich interessiere mich für Montana Y Sol und hätte gerne mehr Informationen.",
    en: "Hello! I'm interested in Montana Y Sol and would like more information.",
    es: "¡Hola! Estoy interesado en Montana Y Sol y me gustaría más información."
  };

  const tooltips = {
    de: "Direkt dem Eigentümer schreiben.",
    en: "Write directly to the owner.",
    es: "Escríbele directamente al propietario."
  };

  const handleClick = () => {
    const message = encodeURIComponent(messages[language]);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
      <AnimatePresence>
        {isHovered && (
          <motion.span
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
            className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap"
            style={{
              background: 'rgba(37, 211, 102, 0.1)',
              border: '1px solid rgba(37, 211, 102, 0.3)',
              color: '#25D366',
              backdropFilter: 'blur(8px)'
            }}
          >
            {tooltips[language]}
          </motion.span>
        )}
      </AnimatePresence>
      <motion.button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300"
        style={{
          background: isHovered ? 'rgba(37, 211, 102, 0.25)' : 'rgba(37, 211, 102, 0.15)',
          border: '1px solid rgba(37, 211, 102, 0.4)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 20px rgba(37, 211, 102, 0.15)'
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: "spring" }}
        whileHover={{ scale: 1.1 }}
        data-testid="whatsapp-button"
        aria-label="WhatsApp"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#25D366">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </motion.button>
    </div>
  );
};

// Consent Context (shared state for cookie consent)
const ConsentContext = createContext();
export const useConsent = () => useContext(ConsentContext);

const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => localStorage.getItem('cookieConsent'));

  const accept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setConsent('accepted');
  };

  const decline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setConsent('declined');
  };

  return (
    <ConsentContext.Provider value={{ consent, hasConsent: consent === 'accepted', accept, decline }}>
      {children}
    </ConsentContext.Provider>
  );
};

// Cookie Consent Banner
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { language } = useLanguage();
  const { consent, accept, decline } = useConsent();

  const texts = {
    de: {
      text: "Diese Website nutzt externe Dienste (Google Maps, Google Drive) die Daten an Server in den USA übertragen können. Mit \"Akzeptieren\" stimmen Sie der Nutzung zu.",
      accept: "Akzeptieren",
      decline: "Ablehnen",
      privacy: "Datenschutz"
    },
    en: {
      text: "This website uses external services (Google Maps, Google Drive) that may transfer data to US servers. By clicking \"Accept\" you consent to their use.",
      accept: "Accept",
      decline: "Decline",
      privacy: "Privacy"
    },
    es: {
      text: "Este sitio web utiliza servicios externos (Google Maps, Google Drive) que pueden transferir datos a servidores en EE.UU. Al hacer clic en \"Aceptar\" consiente su uso.",
      accept: "Aceptar",
      decline: "Rechazar",
      privacy: "Privacidad"
    }
  };

  const t = texts[language];

  useEffect(() => {
    if (!consent) {
      setTimeout(() => setShowBanner(true), 2000);
    }
  }, [consent]);

  const handleAccept = () => {
    accept();
    setShowBanner(false);
  };

  const handleDecline = () => {
    decline();
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <motion.div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl p-6 z-50 shadow-2xl"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      data-testid="cookie-banner"
    >
      <p className="text-sm text-[var(--text-secondary)] mb-4">{t.text}</p>
      <div className="flex gap-3">
        <button onClick={handleAccept} className="btn-primary text-sm py-2 px-4" data-testid="cookie-accept">
          {t.accept}
        </button>
        <button onClick={handleDecline} className="btn-outline text-sm py-2 px-4" data-testid="cookie-decline">
          {t.decline}
        </button>
      </div>
    </motion.div>
  );
};

// Consent-gated iframe wrapper
const ConsentIframe = ({ src, fallbackText, ...props }) => {
  const { hasConsent } = useConsent();

  if (!hasConsent) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--bg-tertiary)] rounded-2xl p-8 text-center">
        <p className="text-[var(--text-secondary)] text-sm">
          {fallbackText || "Externer Inhalt blockiert. Bitte akzeptieren Sie Cookies um diesen Inhalt zu sehen."}
        </p>
      </div>
    );
  }

  return <iframe src={src} {...props} />;
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext, titles }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      className="fixed inset-0 bg-black/95 flex items-center justify-center"
      style={{ zIndex: 9999 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Close button */}
      <button
        className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl"
        style={{ zIndex: 10000 }}
        onClick={onClose}
        data-testid="lightbox-close"
      >
        ×
      </button>

      {/* Counter */}
      <div className="absolute top-6 left-6 text-white/70 text-sm">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous button */}
      <button
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        data-testid="lightbox-prev"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Image */}
      <motion.div
        className="max-w-[90vw] max-h-[80vh] px-16"
        onClick={(e) => e.stopPropagation()}
        key={currentIndex}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <img
          src={images[currentIndex]}
          alt={titles[currentIndex] || ''}
          className="max-w-full max-h-[75vh] object-contain rounded-lg"
        />
        {titles[currentIndex] && (
          <p className="text-center text-white mt-4 font-serif text-xl">{titles[currentIndex]}</p>
        )}
      </motion.div>

      {/* Next button */}
      <button
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        data-testid="lightbox-next"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </motion.div>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'de', label: 'DE', flag: '🇩🇪' },
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'es', label: 'ES', flag: '🇪🇸' }
  ];

  return (
    <div className="relative" data-testid="language-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--border-color)] hover:border-[var(--text-secondary)] transition-colors text-sm"
      >
        <span>{languages.find(l => l.code === language)?.flag}</span>
        <span>{language.toUpperCase()}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full right-0 mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl overflow-hidden shadow-xl z-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`flex items-center gap-3 w-full px-4 py-3 hover:bg-[var(--bg-tertiary)] transition-colors ${
                  language === lang.code ? 'bg-[var(--bg-tertiary)]' : ''
                }`}
                data-testid={`lang-${lang.code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="text-sm">{lang.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Loading Screen Component
const LoadingScreen = ({ onComplete }) => {
  const { logo } = useLanguage();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className="loader"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <motion.img
        src={logo}
        alt="Montana Y Sol"
        className="w-48 h-48 object-contain"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.8 }}
      />
    </motion.div>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, logo, language } = useLanguage();
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`nav ${scrolled ? "scrolled" : ""}`} data-testid="navigation">
      <a href="/" className="flex items-center" data-testid="nav-logo">
        <img src={logo} alt="Montana Y Sol" className="h-16 md:h-36 w-auto object-contain" />
      </a>
      <div className="nav-links">
        <a href="#highlights" className="nav-link" data-testid="nav-highlights">{t.nav.highlights}</a>
        <a href="#impressionen" className="nav-link" data-testid="nav-impressionen">{t.nav.impressionen}</a>
        <a href="#projekt" className="nav-link" data-testid="nav-projekt">{t.nav.projekt || 'Projekt'}</a>
        {/* <a href="#baufortschritt" className="nav-link" data-testid="nav-baufortschritt">{language === 'de' ? 'Baufortschritt' : language === 'es' ? 'Progreso' : 'Progress'}</a> */}
        <a href="#lage" className="nav-link" data-testid="nav-lage">{t.nav.lage}</a>
        <LanguageSwitcher />
        <a href="#kontakt" className="btn-primary" data-testid="nav-contact">
          {t.nav.kontakt}
        </a>
      </div>
      <div className="flex items-center gap-4 md:hidden">
        <LanguageSwitcher />
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-[var(--bg-primary)] z-50 flex flex-col items-center justify-center gap-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <button 
              className="absolute top-6 right-6 text-3xl"
              onClick={() => setMobileMenuOpen(false)}
            >
              ×
            </button>
            <img src={logo} alt="Montana Y Sol" className="h-32 mb-4" />
            <a href="#highlights" className="text-2xl" onClick={() => setMobileMenuOpen(false)}>{t.nav.highlights}</a>
            <a href="#impressionen" className="text-2xl" onClick={() => setMobileMenuOpen(false)}>{t.nav.impressionen}</a>
            <a href="#projekt" className="text-2xl" onClick={() => setMobileMenuOpen(false)}>{t.nav.projekt || 'Projekt'}</a>
            {/* <a href="#baufortschritt" className="text-2xl" onClick={() => setMobileMenuOpen(false)}>{language === 'de' ? 'Baufortschritt' : language === 'es' ? 'Progreso' : 'Progress'}</a> */}
            <a href="#lage" className="text-2xl" onClick={() => setMobileMenuOpen(false)}>{t.nav.lage}</a>
            <a href="#kontakt" className="btn-primary mt-4" onClick={() => setMobileMenuOpen(false)}>{t.nav.kontakt}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Animated Text Component
const AnimatedText = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
};

// Hero Section with Video - No logo in hero, only in nav
const HeroSection = () => {
  const ref = useRef(null);
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  return (
    <section className="min-h-screen relative pt-40 md:pt-24 pb-16 flex flex-col justify-center" ref={ref} data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.wixstatic.com/media/956066_72f52b405f1a49c983079e8e50344839f002.jpg/v1/fill/w_1920,h_1080,enc_auto/file.jpeg')`
          }}
        >
          <div className={`absolute inset-0 ${theme === 'light' ? 'bg-[#FAF6F1]/60' : 'bg-black/50'}`} />
        </div>
      </div>

      <div className="container relative z-10 text-center">
        {/* Hero Title - Text based, no logo */}
        <motion.div
          className="mb-8 mt-16 md:mt-0"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <h1 className={`font-serif text-4xl md:text-7xl mb-4 tracking-wide ${theme === 'light' ? 'text-[#2C2416]' : 'text-white'}`}>
            MONTANA Y SOL
          </h1>
          <p className="text-lg md:text-2xl text-[var(--accent)] font-light tracking-[0.2em] md:tracking-[0.3em] uppercase">
            {t.hero.subtitle}
          </p>
        </motion.div>

        {/* Video Section */}
        <motion.div
          className="mb-8 mx-auto max-w-4xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div 
            className={`relative aspect-video rounded-2xl overflow-hidden border-2 shadow-2xl ${theme === 'light' ? 'border-[#2C2416]/20' : 'border-white/20'}`}
            data-testid="hero-video"
          >
            <video
              src="/hero-video.mp4"
              poster="/hero-poster.jpg"
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              title="Montana Y Sol Video"
            />
          </div>
        </motion.div>

        <motion.p
          className={`text-lg md:text-xl max-w-2xl text-center mx-auto mb-8 ${theme === 'light' ? 'text-[#2C2416]/80' : 'text-white/80'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {t.hero.description}
        </motion.p>

        <motion.div
          className="flex gap-4 flex-wrap justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          <a href="#impressionen" className="btn-primary" data-testid="hero-cta">
            {t.hero.cta}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
          </a>
          <a href="#kontakt" className={`btn-outline ${theme === 'light' ? 'border-[#2C2416]/30 text-[#2C2416] hover:bg-[#2C2416] hover:text-white' : 'border-white/30 text-white hover:bg-white hover:text-black'}`} data-testid="price-request-cta">
            {t.hero.priceRequest || 'Preis auf Anfrage'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          </a>
        </motion.div>
      </div>

      <motion.p
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 text-sm ${theme === 'light' ? 'text-[#2C2416]/60' : 'text-white/60'}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        {t.hero.location}
      </motion.p>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section className="section" id="about" ref={ref} data-testid="about-section">
      <div className="container">
        <div className="about-section">
          <motion.div
            className="about-video video-container rounded-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <img
              src="https://static.wixstatic.com/media/956066_2f8ae5724acd42d2a223f6157705d710~mv2.jpg/v1/fill/w_975,h_536,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/956066_2f8ae5724acd42d2a223f6157705d710~mv2.jpg"
              alt="Montana Y Sol Mallorca"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            className="about-content"
            initial={{ opacity: 0, x: 60 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="section-label">{t.about.label}</p>
            <h2>{t.about.title}</h2>
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
            <p className="text-[var(--accent)]">{t.about.p3}</p>
            <a href="#kontakt" className="btn-outline mt-4" data-testid="about-cta">
              {t.about.cta}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17L17 7M17 7H7M17 7V17" />
              </svg>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Highlights Section
const HighlightsSection = () => {
  const { t } = useLanguage();
  const highlightImages = [
    "https://static.wixstatic.com/media/956066_289c8eb0d5644f1895d5675ac458e81c~mv2.jpg/v1/fill/w_862,h_420,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/956066_289c8eb0d5644f1895d5675ac458e81c~mv2.jpg",
    "https://static.wixstatic.com/media/956066_2073127d71664c6b9076e226a7919c49~mv2.jpg/v1/fill/w_862,h_372,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/956066_2073127d71664c6b9076e226a7919c49~mv2.jpg",
    "https://static.wixstatic.com/media/956066_c75249fa5bd24187af5f6b1418b0fdd2~mv2.jpg/v1/fill/w_800,h_533,al_c,q_85,enc_avif,quality_auto/956066_c75249fa5bd24187af5f6b1418b0fdd2~mv2.jpg",
    "https://static.wixstatic.com/media/956066_8949faf3bdd7401593bed31af7a11901~mv2.jpg/v1/fill/w_800,h_533,al_c,q_80,enc_avif,quality_auto/956066_8949faf3bdd7401593bed31af7a11901~mv2.jpg"
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section className="section bg-[var(--bg-secondary)]" id="highlights" ref={ref} data-testid="highlights-section">
      <div className="container">
        <AnimatedText className="section-header text-center mb-16">
          <p className="section-label">{t.highlights.label}</p>
          <h2 className="section-title">
            {t.highlights.title} <em>{t.highlights.titleHighlight}</em>
          </h2>
        </AnimatedText>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {t.highlights.items.map((item, index) => (
            <motion.div
              key={item.number}
              className="group relative rounded-2xl overflow-hidden aspect-[4/3] cursor-pointer"
              initial={{ opacity: 0, y: 60 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              data-testid={`highlight-${item.number}`}
            >
              <img
                src={highlightImages[index]}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="text-[var(--accent)] text-sm font-medium">{item.number}</span>
                <h3 className="font-serif text-2xl md:text-3xl text-white mt-2 mb-3">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Impressionen Gallery Section with Lightbox
const ImpressionenSection = () => {
  const { t } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = [
    { src: "/assets/gallery/PS_Roof4_fotorealistisch.png", large: true },
    { src: "/assets/gallery/PS_InsideFacade_fotorealistisch.png", large: false },
    { src: "/assets/gallery/PS_FirstFloor_fotorealistisch.png", large: false },
    { src: "/assets/gallery/PS_StreetView_fotorealistisch.png", large: true },
    { src: "/assets/gallery/PS_Pool_View_fotorealistisch.png", large: false },
    { src: "/assets/gallery/PS_Cinema_fotorealistisch.png", large: false },
    { src: "/assets/gallery/PS_Roof_fotorealistisch.png", large: true },
    { src: "/assets/gallery/PS_LucasView_fotorealistisch.png", large: false }
  ];

  const imageSrcs = images.map(img => img.src);
  const imageTitles = t.impressionen.images.map(img => img.title);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <section className="section" id="impressionen" ref={ref} data-testid="impressionen-section">
      <div className="container">
        <AnimatedText className="section-header">
          <p className="section-label">{t.impressionen.label}</p>
          <h2 className="section-title">
            <em>{t.impressionen.title}</em>
          </h2>
          <p className="text-[var(--text-secondary)] mt-4">{t.impressionen.subtitle}</p>
        </AnimatedText>

        <div className="work-grid">
          {images.map((image, index) => (
            <motion.div
              key={index}
              className={`work-item card-hover img-zoom cursor-pointer ${image.large ? "large" : ""}`}
              initial={{ opacity: 0, y: 60 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              onClick={() => openLightbox(index)}
              data-testid={`impression-${index}`}
            >
              <img
                src={image.src}
                alt={t.impressionen.images[index]?.title || ''}
                className="work-item-image"
              />
              <div className="work-item-overlay">
                <h3 className="work-item-title">{t.impressionen.images[index]?.title}</h3>
                <p className="text-white/60 text-sm mt-2">Klicken zum Vergrößern</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={imageSrcs}
            titles={imageTitles}
            currentIndex={currentImageIndex}
            onClose={closeLightbox}
            onPrev={prevImage}
            onNext={nextImage}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

// Project Details Section
const ProjectDetailsSection = () => {
  const { language } = useLanguage();
  
  const texts = {
    de: {
      label: "Das Projekt",
      title: "Projekt",
      titleHighlight: "Details",
      features: [
        { icon: "🏠", value: "4", label: "Luxus-Einheiten" },
        { icon: "📐", value: "~150", label: "m² Wohnfläche/Haus" },
        { icon: "🏊", value: "4", label: "Eigene Infinity Pools" },
        { icon: "🌳", value: "~1.100", label: "m² Grundstücksfläche" }
      ],
      amenities: [
        "Private Dachterrassen",
        "Outdoor-Kino",
        "Mediterrane Gärten",
        "Tiefgarage",
        "Smart Home System",
        "Klimaanlage",
        "Fußbodenheizung",
        "Hochwertige Einbauküche"
      ],
      amenitiesTitle: "Ausstattung"
    },
    en: {
      label: "The Project",
      title: "Project",
      titleHighlight: "Details",
      features: [
        { icon: "🏠", value: "4", label: "Luxury Units" },
        { icon: "📐", value: "~150", label: "sqm Living Space/Unit" },
        { icon: "🏊", value: "4", label: "Private Infinity Pools" },
        { icon: "🌳", value: "~1,100", label: "sqm Total Plot" }
      ],
      amenities: [
        "Private Rooftop Terraces",
        "Outdoor Cinema",
        "Mediterranean Gardens",
        "Underground Parking",
        "Smart Home System",
        "Air Conditioning",
        "Underfloor Heating",
        "Premium Kitchen"
      ],
      amenitiesTitle: "Amenities"
    },
    es: {
      label: "El Proyecto",
      title: "Detalles del",
      titleHighlight: "Proyecto",
      features: [
        { icon: "🏠", value: "4", label: "Unidades de Lujo" },
        { icon: "📐", value: "~150", label: "m² Superficie/Unidad" },
        { icon: "🏊", value: "4", label: "Piscinas Infinity Privadas" },
        { icon: "🌳", value: "~1.100", label: "m² Parcela Total" }
      ],
      amenities: [
        "Terrazas Privadas",
        "Cine al Aire Libre",
        "Jardines Mediterráneos",
        "Garaje Subterráneo",
        "Sistema Domótico",
        "Aire Acondicionado",
        "Suelo Radiante",
        "Cocina de Alta Gama"
      ],
      amenitiesTitle: "Equipamiento"
    }
  };

  const t = texts[language];
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section className="section" id="projekt" ref={ref} data-testid="project-section">
      <div className="container">
        <AnimatedText className="section-header text-center mb-16">
          <p className="section-label">{t.label}</p>
          <h2 className="section-title">
            {t.title} <em>{t.titleHighlight}</em>
          </h2>
        </AnimatedText>

        {/* Key Figures */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {t.features.map((feature, index) => (
            <motion.div
              key={index}
              className="text-center p-8 rounded-2xl bg-[var(--bg-secondary)]"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <span className="text-4xl mb-4 block">{feature.icon}</span>
              <p className="text-4xl font-serif text-[var(--accent)] mb-2">{feature.value}</p>
              <p className="text-[var(--text-secondary)] text-sm">{feature.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Amenities */}
        <motion.div
          className="bg-[var(--bg-secondary)] rounded-2xl p-8 md:p-12"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="font-serif text-2xl mb-8 text-center">{t.amenitiesTitle}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {t.amenities.map((amenity, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-xl bg-[var(--bg-primary)]">
                <svg className="text-[var(--accent)] flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span className="text-sm">{amenity}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Construction Timeline — GSAP ScrollTrigger + Image Sequence (Trackpad-optimiert)
// 11 JPGs (~10MB) statt Video (13MB) — performanter, kein Video-Decode nötig
// Opacity crossfade + subtle zoom via GSAP timeline

const TIMELINE_IMAGES = [
  '/timeline-01.jpg', '/timeline-02.jpg', '/timeline-02b.jpg',
  '/timeline-03.jpg', '/timeline-04.jpg', '/timeline-05.jpg',
  '/timeline-05b.jpg', '/timeline-06.jpg', '/timeline-06b.jpg',
  '/timeline-07.jpg', '/timeline-08.jpg',
];

// 8 Phasen mapped auf 11 Bilder (manche Phasen haben Zwischenschritte)
const PHASE_TO_FRAMES = [
  [0],        // Phase 1: Grundstück (frame 0)
  [1, 2],     // Phase 2: Fundament (frames 1-2)
  [3],        // Phase 3: Erdgeschoss (frame 3)
  [4],        // Phase 4: Rohbau (frame 4)
  [5, 6],     // Phase 5: Dach & Fenster (frames 5-6)
  [7, 8],     // Phase 6: Fassade & Außen (frames 7-8)
  [9],        // Phase 7: Garten & Pool (frame 9)
  [10],       // Phase 8: Fertiges Haus (frame 10)
];

const TimelineSection = () => {
  const { language } = useLanguage();
  const containerRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [activePhase, setActivePhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const activePhaseRef = useRef(0);

  const texts = {
    de: {
      label: "Baufortschritt",
      title: "Projekt",
      titleHighlight: "Timeline",
      phases: [
        { title: "Das Grundstück", description: "Das unbebaute Grundstück in Andratx mit Blick auf die Tramuntana." },
        { title: "Fundament & Schalung", description: "Gründungsarbeiten und Fundamenterstellung für ein stabiles Gebäude." },
        { title: "Rohbau Erdgeschoss", description: "Errichtung der Mauern und Struktur des Erdgeschosses." },
        { title: "Rohbau & Flachdach", description: "Fertigstellung des Rohbaus mit Flachdachkonstruktion." },
        { title: "Dach & Fenstereinbau", description: "Einbau der hochwertigen Fenster und Dacharbeiten." },
        { title: "Fassade & Außenanlagen", description: "Fassadengestaltung und Beginn der Außenanlagen." },
        { title: "Gartenanlage & Pool", description: "Fertigstellung der Poolanlage und mediterranen Gartenlandschaft." },
        { title: "Ihr neues Zuhause", description: "Das fertige Gebäude in der Abendsonne von Mallorca." },
      ]
    },
    en: {
      label: "Construction Progress",
      title: "Project",
      titleHighlight: "Timeline",
      phases: [
        { title: "The Plot", description: "The undeveloped plot in Andratx with views of the Tramuntana mountains." },
        { title: "Foundation & Formwork", description: "Foundation work creating a stable base for the building." },
        { title: "Ground Floor Structure", description: "Construction of the ground floor walls and structure." },
        { title: "Structure & Flat Roof", description: "Completion of the shell with flat roof construction." },
        { title: "Roof & Window Installation", description: "Installation of premium windows and roofing work." },
        { title: "Facade & Exterior", description: "Facade design and beginning of exterior landscaping." },
        { title: "Garden & Pool", description: "Completion of the pool area and Mediterranean garden landscape." },
        { title: "Your New Home", description: "The finished building in the Mallorcan evening sun." },
      ]
    },
    es: {
      label: "Progreso de Construcción",
      title: "Cronograma del",
      titleHighlight: "Proyecto",
      phases: [
        { title: "El Terreno", description: "La parcela sin edificar en Andratx con vistas a la Tramuntana." },
        { title: "Cimentación", description: "Trabajos de cimentación para una base estable del edificio." },
        { title: "Estructura Planta Baja", description: "Construcción de muros y estructura de la planta baja." },
        { title: "Estructura y Techo", description: "Finalización de la estructura con techo plano." },
        { title: "Techo y Ventanas", description: "Instalación de ventanas premium y trabajos de cubierta." },
        { title: "Fachada y Exteriores", description: "Diseño de fachada e inicio del paisajismo exterior." },
        { title: "Jardín y Piscina", description: "Finalización de la piscina y jardín mediterráneo." },
        { title: "Su Nuevo Hogar", description: "El edificio terminado bajo el sol de la tarde mallorquina." },
      ]
    }
  };

  const t = texts[language];

  // Preload all images
  useEffect(() => {
    let loaded = 0;
    TIMELINE_IMAGES.forEach(src => {
      const img = new Image();
      img.onload = () => { loaded++; if (loaded === TIMELINE_IMAGES.length) setImagesLoaded(true); };
      img.onerror = () => { loaded++; if (loaded === TIMELINE_IMAGES.length) setImagesLoaded(true); };
      img.src = src;
    });
  }, []);

  // GSAP ScrollTrigger — image sequence crossfade
  useLayoutEffect(() => {
    if (!imagesLoaded || !containerRef.current) return;

    const totalFrames = TIMELINE_IMAGES.length;
    const frameDuration = 1 / (totalFrames - 1);

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: '+=25000',
          pin: true,
          scrub: 4,
          anticipatePin: 1,
          onUpdate: (self) => {
            setProgress(self.progress);

            // Determine active phase from progress
            const frameIndex = Math.min(
              Math.floor(self.progress * totalFrames),
              totalFrames - 1
            );
            let phase = 0;
            for (let p = 0; p < PHASE_TO_FRAMES.length; p++) {
              if (PHASE_TO_FRAMES[p].includes(frameIndex)) { phase = p; break; }
              if (frameIndex > PHASE_TO_FRAMES[p][PHASE_TO_FRAMES[p].length - 1]) { phase = p; }
            }
            if (phase !== activePhaseRef.current) {
              activePhaseRef.current = phase;
              setActivePhase(phase);
            }
          }
        }
      });

      // Image crossfade sequence
      for (let i = 1; i < totalFrames; i++) {
        const startTime = (i - 1) * frameDuration;
        tl.fromTo(`#tl-frame-${i}`,
          { opacity: 0 },
          { opacity: 1, duration: frameDuration, ease: 'none' },
          startTime
        );
        // Subtle zoom for depth
        tl.fromTo(`#tl-frame-${i}`,
          { scale: 1.04 },
          { scale: 1, duration: frameDuration, ease: 'none' },
          startTime
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, [imagesLoaded]);

  return (
    <section
      ref={containerRef}
      id="baufortschritt"
      data-testid="timeline-section"
    >
      <div className="w-full h-screen overflow-hidden relative">

        {/* Image layers */}
        {TIMELINE_IMAGES.map((src, i) => (
          <div
            key={src}
            id={`tl-frame-${i}`}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: i === 0 ? 1 : 0,
              willChange: 'opacity, transform',
            }}
          />
        ))}

        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 50%, rgba(0,0,0,0.3) 100%)',
          zIndex: 2,
        }} />

        {/* Loading */}
        {!imagesLoaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[var(--bg-primary)]" style={{ zIndex: 5 }}>
            <p className="text-xs tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--text-muted)' }}>{t.label}</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-8" style={{ color: 'var(--text-primary)', fontWeight: 200 }}>
              {t.title} <em>{t.titleHighlight}</em>
            </h2>
            <div className="w-16 h-px animate-pulse" style={{ background: 'var(--accent)' }} />
          </div>
        )}

        {/* Phase counter (top-left) */}
        {imagesLoaded && (
          <div className="absolute top-6 left-6 md:top-8 md:left-10 pointer-events-none" style={{ zIndex: 10 }}>
            <div className="flex items-baseline gap-2">
              <span style={{
                fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 200,
                fontSize: window.innerWidth < 768 ? '40px' : '56px', lineHeight: 1,
                color: '#fff', textShadow: '0 2px 20px rgba(0,0,0,0.5)',
              }}>
                {String(activePhase + 1).padStart(2, '0')}
              </span>
              <span style={{ fontWeight: 300, fontSize: '14px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.05em' }}>
                / 08
              </span>
            </div>
          </div>
        )}

        {/* Phase label (bottom-center) */}
        {imagesLoaded && (
          <div className="absolute bottom-16 md:bottom-20 left-0 w-full text-center pointer-events-none" style={{ zIndex: 10 }}>
            <motion.div
              key={activePhase}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              <span style={{
                fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 300,
                fontSize: window.innerWidth < 768 ? '12px' : '14px',
                letterSpacing: '0.25em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.85)', textShadow: '0 2px 16px rgba(0,0,0,0.6)',
                padding: '10px 24px', background: 'rgba(0,0,0,0.2)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px',
              }}>
                {t.phases[activePhase]?.title}
              </span>
            </motion.div>
            <motion.p
              key={`desc-${activePhase}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-3"
              style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', maxWidth: '400px',
                margin: '12px auto 0', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
            >
              {t.phases[activePhase]?.description}
            </motion.p>
          </div>
        )}

        {/* Progress bar */}
        {imagesLoaded && (
          <div className="absolute bottom-0 left-0 w-full pointer-events-none" style={{ zIndex: 10, height: '2px' }}>
            <div style={{
              width: (progress * 100) + '%', height: '100%',
              background: 'linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.4))',
            }} />
          </div>
        )}

        {/* Phase dots */}
        {imagesLoaded && (
          <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 flex flex-col gap-3 pointer-events-none" style={{ zIndex: 10 }}>
            {[0,1,2,3,4,5,6,7].map(i => (
              <div key={i} className="transition-all duration-300" style={{
                width: i === activePhase ? '12px' : '6px', height: '6px', borderRadius: '3px',
                background: i === activePhase ? 'rgba(255,255,255,0.8)' : i < activePhase ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)',
              }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Drone Story Scroll Animation — Cinematic 3-Act Video Experience (GSAP ScrollTrigger)
// Act boundaries as fractions of video duration (0-1):
// Act 1: Orbit (frames 0-15, ~0-0.50), Act 2: Fly-In (frames 16-27, ~0.50-0.875), Act 3: Finale (frames 28-31, ~0.875-1.0)
const DRONE_ACT_BOUNDARIES = [0, 0.50, 0.875, 1.0];

const DroneStorySection = () => {
  const { language } = useLanguage();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [activeAct, setActiveAct] = useState(0);
  const [progress, setProgress] = useState(0);

  const texts = {
    de: {
      label: "Erleben Sie es selbst",
      title: "Von der Baustelle",
      titleHighlight: "zum Traumabend",
      acts: [
        { title: "Drohnen-Orbit", description: "360° rund um die Baustelle — von der Planung bis zur fertigen Fassade." },
        { title: "Anflug bei Sonnenuntergang", description: "Die Drohne fliegt näher — Dämmerung über der Tramuntana." },
        { title: "Abend auf der Dachterrasse", description: "Ankommen. Genießen. Zuhause sein." },
      ]
    },
    en: {
      label: "Experience it yourself",
      title: "From construction site",
      titleHighlight: "to dream evening",
      acts: [
        { title: "Drone Orbit", description: "360° around the construction site — from planning to finished facade." },
        { title: "Approach at Sunset", description: "The drone flies closer — dusk over the Tramuntana mountains." },
        { title: "Evening on the Rooftop", description: "Arrive. Enjoy. Be home." },
      ]
    },
    es: {
      label: "Vívalo usted mismo",
      title: "De la obra",
      titleHighlight: "a la velada soñada",
      acts: [
        { title: "Órbita del Dron", description: "360° alrededor de la obra — desde la planificación hasta la fachada terminada." },
        { title: "Aproximación al Atardecer", description: "El dron se acerca — anochecer sobre la Tramuntana." },
        { title: "Velada en la Azotea", description: "Llegar. Disfrutar. Estar en casa." },
      ]
    }
  };

  const t = texts[language];

  // Draw video frame to canvas (object-fit: cover)
  const drawVideoFrame = React.useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !video.videoWidth) return;

    const ctx = canvas.getContext('2d');
    const cw = canvas.width;
    const ch = canvas.height;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const canvasRatio = cw / ch;
    const videoRatio = vw / vh;

    let drawW, drawH, offsetX, offsetY;
    if (videoRatio > canvasRatio) {
      drawH = ch; drawW = ch * videoRatio;
      offsetX = (cw - drawW) / 2; offsetY = 0;
    } else {
      drawW = cw; drawH = cw / videoRatio;
      offsetX = 0; offsetY = (ch - drawH) / 2;
    }

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(video, offsetX, offsetY, drawW, drawH);
  }, []);

  // Resize canvas to viewport
  const resizeCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
    drawVideoFrame();
  }, [drawVideoFrame]);

  // Load video
  useEffect(() => {
    const video = document.createElement('video');
    video.src = '/drone-flight.mp4';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.style.display = 'none';
    document.body.appendChild(video);
    videoRef.current = video;

    const onReady = () => setVideoReady(true);
    video.addEventListener('canplaythrough', onReady);

    return () => {
      video.removeEventListener('canplaythrough', onReady);
      video.remove();
    };
  }, []);

  // GSAP ScrollTrigger — pin + scrub
  useLayoutEffect(() => {
    if (!videoReady) return;

    resizeCanvas();
    const video = videoRef.current;
    const duration = video.duration;

    video.currentTime = 0;
    video.addEventListener('seeked', drawVideoFrame);
    drawVideoFrame();

    const ctx = gsap.context(() => {
      const obj = { time: 0 };

      gsap.to(obj, {
        time: duration,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=5000",        // 5000px — more scroll space for 32 keyframes
          pin: true,
          scrub: 1.5,           // 1.5s smoothing for cinematic feel
          anticipatePin: 1,
          onUpdate: (self) => {
            const currentTime = self.progress * duration;
            video.currentTime = currentTime;
            setProgress(self.progress);

            // Determine act from boundaries
            for (let i = 0; i < DRONE_ACT_BOUNDARIES.length - 1; i++) {
              if (self.progress >= DRONE_ACT_BOUNDARIES[i] && self.progress < DRONE_ACT_BOUNDARIES[i + 1]) {
                setActiveAct(i);
                break;
              }
            }
            if (self.progress >= 0.875) setActiveAct(2);
          }
        }
      });
    }, containerRef);

    const handleResize = () => {
      resizeCanvas();
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      ctx.revert();
      video.removeEventListener('seeked', drawVideoFrame);
      window.removeEventListener('resize', handleResize);
    };
  }, [videoReady, resizeCanvas, drawVideoFrame]);

  return (
    <section
      ref={containerRef}
      id="drohnenflug"
      data-testid="drone-story-section"
    >
      <div className="w-full h-screen overflow-hidden relative">

        {/* Canvas */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{ display: videoReady ? 'block' : 'none' }}
        />

        {/* Cinematic letterbox bars */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8%', background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '12%', background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }} />
        </div>

        {/* Loading state */}
        {!videoReady && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[var(--bg-primary)]" style={{ zIndex: 5 }}>
            <p className="text-xs tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--text-muted)' }}>
              {t.label}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl mb-8" style={{ color: 'var(--text-primary)', fontWeight: 200 }}>
              {t.title} <em>{t.titleHighlight}</em>
            </h2>
            <div className="w-16 h-px animate-pulse" style={{ background: 'var(--accent)' }} />
          </div>
        )}

        {/* Act indicator (top-left) */}
        {videoReady && (
          <div className="absolute top-6 left-6 md:top-8 md:left-10 pointer-events-none" style={{ zIndex: 10 }}>
            <motion.div
              key={activeAct}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <p style={{
                fontSize: '10px',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.35)',
                marginBottom: '6px',
              }}>
                {language === 'de' ? 'Akt' : language === 'es' ? 'Acto' : 'Act'} {activeAct + 1} / 3
              </p>
              <p style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 300,
                fontSize: window.innerWidth < 768 ? '16px' : '20px',
                color: 'rgba(255,255,255,0.85)',
                textShadow: '0 2px 12px rgba(0,0,0,0.5)',
                letterSpacing: '0.08em',
              }}>
                {t.acts[activeAct]?.title}
              </p>
            </motion.div>
          </div>
        )}

        {/* Description (bottom-center) */}
        {videoReady && (
          <div className="absolute bottom-12 md:bottom-16 left-0 w-full text-center pointer-events-none" style={{ zIndex: 10 }}>
            <motion.p
              key={`drone-desc-${activeAct}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                fontSize: '13px',
                fontWeight: 300,
                color: 'rgba(255,255,255,0.55)',
                maxWidth: '460px',
                margin: '0 auto',
                textShadow: '0 1px 8px rgba(0,0,0,0.6)',
                letterSpacing: '0.03em',
                padding: '0 24px',
              }}
            >
              {t.acts[activeAct]?.description}
            </motion.p>
          </div>
        )}

        {/* Progress dots (right side) */}
        {videoReady && (
          <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 flex flex-col gap-3 pointer-events-none" style={{ zIndex: 10 }}>
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="transition-all duration-300"
                style={{
                  width: i === activeAct ? '16px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  background: i === activeAct ? 'rgba(255,255,255,0.8)' : i < activeAct ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>
        )}

        {/* Bottom progress bar (gold accent) */}
        {videoReady && (
          <div className="absolute bottom-0 left-0 w-full pointer-events-none" style={{ zIndex: 10, height: '2px' }}>
            <div style={{
              width: (progress * 100) + '%', height: '100%',
              background: 'linear-gradient(90deg, rgba(201,162,39,0.2), rgba(201,162,39,0.7))',
            }} />
          </div>
        )}
      </div>
    </section>
  );
};

// Location Section with Google Maps
const LocationSection = () => {
  const { t, language } = useLanguage();
  const icons = [
    <svg key="loc" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>,
    <svg key="sea" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>,
    <svg key="mount" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>,
    <svg key="sust" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section className="section" id="lage" ref={ref} data-testid="location-section">
      <div className="container">
        <AnimatedText className="section-header text-center mb-16">
          <p className="section-label">{t.location.label}</p>
          <h2 className="section-title">
            {t.location.title} <em>{t.location.titleHighlight}</em> {t.location.titleEnd}
          </h2>
        </AnimatedText>

        {/* Google Maps */}
        <motion.div
          className="rounded-2xl overflow-hidden mb-16 aspect-[21/9]"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          data-testid="google-maps"
        >
          <ConsentIframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12345.67890!2d2.4197!3d39.5753!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1297910c02c0e8cf%3A0x400eef17e2f7930!2s07150%20Andratx%2C%20Balearic%20Islands%2C%20Spain!5e0!3m2!1sen!2sde!4v1699999999999!5m2!1sen!2sde"
            fallbackText={language === 'de' ? 'Bitte akzeptieren Sie Cookies um die Karte zu sehen.' : language === 'es' ? 'Por favor, acepte las cookies para ver el mapa.' : 'Please accept cookies to view the map.'}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Montana Y Sol Location - Andratx, Mallorca"
            sandbox="allow-scripts allow-same-origin"
          />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {t.location.features.map((feature, index) => (
            <motion.div
              key={index}
              className="text-center p-8 rounded-2xl bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-all duration-300"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              data-testid={`feature-${index}`}
            >
              <div className="text-[var(--accent)] mb-4 flex justify-center">
                {icons[index]}
              </div>
              <h3 className="font-serif text-lg mb-2">{feature.title}</h3>
              <p className="text-[var(--text-secondary)] text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Activities */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <p className="text-[var(--text-muted)] uppercase tracking-widest text-sm mb-6">{t.location.activitiesLabel}</p>
          <div className="flex flex-wrap justify-center gap-4">
            {t.location.activities.map((activity, i) => (
              <span 
                key={i} 
                className="px-6 py-3 rounded-full border border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all duration-300 cursor-default"
              >
                {activity}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Lifestyle Section - Like original website */}
        <motion.div
          className="mt-24"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="flex items-baseline gap-4 mb-12">
            <span className="font-serif text-6xl text-[var(--accent)]/30">06</span>
            <div>
              <h3 className="font-serif text-3xl">{t.location.lifestyleTitle || 'Ihr Lifestyle'}</h3>
              <div className="w-16 h-0.5 bg-[var(--accent)] mt-2"></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { 
                src: "/assets/location/port_andratx.jpeg", 
                location: { de: "PORT D'ANDRATX", en: "PORT D'ANDRATX", es: "PORT D'ANDRATX" },
                distance: "5 MIN",
                title: { de: "Port d'Andratx", en: "Port d'Andratx", es: "Port d'Andratx" },
                description: { 
                  de: "Das St. Tropez Spaniens - legendäre Sonnenuntergänge am Yachthafen, Michelin-Restaurants und eine weltoffene Gemeinschaft direkt am Wasser.",
                  en: "The St. Tropez of Spain - legendary sunsets at the marina, Michelin restaurants and a cosmopolitan community right on the water.",
                  es: "El St. Tropez de España - atardeceres legendarios en el puerto deportivo, restaurantes Michelin y una comunidad cosmopolita junto al agua."
                }
              },
              { 
                src: "/assets/location/golf.jpeg", 
                location: { de: "GOLF DE ANDRATX", en: "GOLF DE ANDRATX", es: "GOLF DE ANDRATX" },
                distance: "10 MIN",
                title: { de: "Golf de Andratx", en: "Golf de Andratx", es: "Golf de Andratx" },
                description: { 
                  de: "Einer der exklusivsten Golfplätze der Insel mit atemberaubenden Ausblicken auf die Serra de Tramuntana und das Mittelmeer.",
                  en: "One of the most exclusive golf courses on the island with breathtaking views of the Serra de Tramuntana and the Mediterranean.",
                  es: "Uno de los campos de golf más exclusivos de la isla con vistas impresionantes a la Serra de Tramuntana y el Mediterráneo."
                }
              },
              { 
                src: "/assets/location/dragonera.jpeg", 
                location: { de: "SA DRAGONERA", en: "SA DRAGONERA", es: "SA DRAGONERA" },
                distance: "15 MIN",
                title: { de: "Sa Dragonera", en: "Sa Dragonera", es: "Sa Dragonera" },
                description: { 
                  de: "Die unbewohnte Dracheninsel - ein Naturschutzgebiet für Wanderungen, Bootsausflüge und unberührte Natur vor der Küste.",
                  en: "The uninhabited Dragon Island - a nature reserve for hiking, boat trips and untouched nature off the coast.",
                  es: "La isla deshabitada del Dragón - una reserva natural para senderismo, excursiones en barco y naturaleza virgen frente a la costa."
                }
              },
              { 
                src: "/assets/location/andratx_dorf.webp", 
                location: { de: "ANDRATX DORF", en: "ANDRATX VILLAGE", es: "PUEBLO DE ANDRATX" },
                distance: "2 MIN",
                title: { de: "Andratx Dorf", en: "Andratx Village", es: "Pueblo de Andratx" },
                description: { 
                  de: "Authentisches mallorquinisches Dorfleben - Wochenmärkte, lokale Cafés, jahrhundertealte Kirche und das echte Inselleben abseits des Tourismus.",
                  en: "Authentic Mallorcan village life - weekly markets, local cafés, centuries-old church and real island life away from tourism.",
                  es: "Auténtica vida de pueblo mallorquín - mercados semanales, cafés locales, iglesia centenaria y vida isleña real lejos del turismo."
                }
              },
              {
                src: "/assets/location/tramuntana.jpeg",
                location: { de: "SERRA DE TRAMUNTANA", en: "SERRA DE TRAMUNTANA", es: "SERRA DE TRAMUNTANA" },
                distance: "5 MIN",
                title: { de: "Serra de Tramuntana", en: "Serra de Tramuntana", es: "Serra de Tramuntana" },
                description: {
                  de: "UNESCO-Weltkulturerbe - spektakuläre Wanderwege, malerische Bergdörfer und die dramatischste Landschaft der Balearen.",
                  en: "UNESCO World Heritage Site - spectacular hiking trails, picturesque mountain villages and the most dramatic landscape of the Balearics.",
                  es: "Patrimonio Mundial de la UNESCO - senderos espectaculares, pintorescos pueblos de montaña y el paisaje más dramático de las Baleares."
                }
              },
              {
                src: "/assets/location/beaches.png",
                location: { de: "STRÄNDE & BUCHTEN", en: "BEACHES & COVES", es: "PLAYAS Y CALAS" },
                distance: "5 MIN",
                title: { de: "Strände & Buchten", en: "Beaches & Coves", es: "Playas y Calas" },
                description: {
                  de: "Von belebten Sandstränden mit Strandbar bis zu abgeschiedenen Felsbuchten, die nur per Boot erreichbar sind – die Küste rund um Andratx bietet für jeden den perfekten Tag am Meer. Alles in wenigen Minuten vom Haus erreichbar.",
                  en: "From lively sandy beaches with beach bars to secluded rocky coves accessible only by boat – the coast around Andratx offers the perfect day by the sea for everyone. All reachable within minutes from the house.",
                  es: "Desde animadas playas de arena con chiringuitos hasta calas rocosas apartadas accesibles solo en barco – la costa alrededor de Andratx ofrece el día perfecto junto al mar para todos. Todo a pocos minutos de la casa."
                }
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-[var(--bg-secondary)] rounded-2xl overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.title[language]}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[var(--accent)] text-sm font-medium tracking-wider mb-2">
                    <span>{item.location[language]}</span>
                    <span>·</span>
                    <span>{item.distance}</span>
                  </div>
                  <h4 className="font-serif text-xl mb-3">{item.title[language]}</h4>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{item.description[language]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    subject: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const priceRequestText = {
    de: "Ich interessiere mich für eine Einheit in Montana Y Sol und möchte mehr über die Preise erfahren.",
    en: "I am interested in a unit at Montana Y Sol and would like to learn more about pricing.",
    es: "Estoy interesado en una unidad en Montana Y Sol y me gustaría saber más sobre los precios."
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      setSubmitted(true);
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitError(language === 'de' ? 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.' : 
                     language === 'en' ? 'Failed to send message. Please try again.' : 
                     'No se pudo enviar el mensaje. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePriceRequest = () => {
    setFormData({
      ...formData,
      subject: language === 'de' ? 'Preisanfrage' : language === 'en' ? 'Price Inquiry' : 'Consulta de Precio',
      message: priceRequestText[language]
    });
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section className="section" id="kontakt" ref={ref} data-testid="contact-section">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="section-label">{t.contact.label}</p>
            <h2 className="section-title mb-8">
              {t.contact.title} <em>{t.contact.titleHighlight}</em>
            </h2>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="text-[var(--accent)]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium mb-1">{t.contact.locationLabel}</h4>
                  <p className="text-[var(--text-secondary)]">Andratx, Mallorca</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-[var(--accent)]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium mb-1">{t.contact.emailLabel}</h4>
                  <a href="mailto:info@5dimensional.de" className="text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors">
                    info@5dimensional.de
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-[var(--accent)]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium mb-1">{t.contact.phoneLabel}</h4>
                  <a href="tel:+4916028388886" className="text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors">
                    +49 160 28 38 886
                  </a>
                </div>
              </div>
            </div>

            {/* Price Request Button */}
            <button
              onClick={handlePriceRequest}
              className="btn-primary w-full justify-center mb-4"
              data-testid="price-request-btn"
            >
              {t.hero.priceRequest || 'Preis auf Anfrage'}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
              </svg>
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {submitted ? (
              <div className="h-full flex items-center justify-center text-center p-8 rounded-2xl bg-[var(--bg-secondary)]">
                <div>
                  <div className="text-[var(--accent)] text-6xl mb-4">✓</div>
                  <h3 className="font-serif text-2xl mb-2">{t.contact.form.success}</h3>
                  <p className="text-[var(--text-secondary)]">{t.contact.form.successMessage}</p>
                </div>
              </div>
            ) : (
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder={t.contact.form.name}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent)] focus:outline-none transition-colors"
                    data-testid="input-name"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder={t.contact.form.email}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent)] focus:outline-none transition-colors"
                    data-testid="input-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t.contact.form.phone}
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent)] focus:outline-none transition-colors"
                    data-testid="input-phone"
                  />
                  <input
                    type="text"
                    name="address"
                    placeholder={t.contact.form.address}
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent)] focus:outline-none transition-colors"
                    data-testid="input-address"
                  />
                </div>
                <input
                  type="text"
                  name="subject"
                  placeholder={t.contact.form.subject}
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent)] focus:outline-none transition-colors"
                  data-testid="input-subject"
                />
                <textarea
                  name="message"
                  placeholder={t.contact.form.message}
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  required
                  className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent)] focus:outline-none transition-colors resize-none"
                  data-testid="input-message"
                />
                {submitError && (
                  <div className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">
                    {submitError}
                  </div>
                )}
                <button 
                  type="submit" 
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed" 
                  disabled={isSubmitting}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      {language === 'de' ? 'Wird gesendet...' : language === 'en' ? 'Sending...' : 'Enviando...'}
                    </>
                  ) : (
                    <>
                      {t.contact.form.submit}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const { t, logo } = useLanguage();

  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={logo} alt="Montana Y Sol" className="h-20 mb-4" />
            <p>{t.footer.description}</p>
          </div>

          <div className="footer-links">
            <h4>{t.footer.navigation}</h4>
            <ul>
              <li><a href="#highlights">{t.nav.highlights}</a></li>
              <li><a href="#impressionen">{t.nav.impressionen}</a></li>
              <li><a href="#projekt">{t.nav.projekt || 'Projekt'}</a></li>
              <li><a href="#lage">{t.nav.lage}</a></li>
              <li><a href="#kontakt">{t.nav.kontakt}</a></li>
            </ul>
          </div>

          <div className="footer-links">
            <h4>{t.footer.activities}</h4>
            <ul>
              {t.location.activities.slice(0, 4).map((activity, i) => (
                <li key={i}><a href="#lage">{activity}</a></li>
              ))}
            </ul>
          </div>

          <div className="footer-links">
            <h4>{t.footer.contact}</h4>
            <ul>
              <li><a href="mailto:info@5dimensional.de">info@5dimensional.de</a></li>
              <li><a href="tel:+4916028388886">+49 160 28 38 886</a></li>
              <li><span className="text-[var(--text-secondary)]">Andratx, Mallorca</span></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">
            © 2025 Montana Y Sol. {t.footer.copyright}
          </p>
          <p className="text-[var(--text-muted)] text-sm">
            {t.footer.tagline}
          </p>
        </div>

        <motion.div
          className="text-center mt-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <p className="text-[var(--text-muted)] text-sm mb-4">{t.footer.finalTagline}</p>
          <img src={logo} alt="Montana Y Sol" className="h-40 mx-auto opacity-30" />
        </motion.div>
      </div>
    </footer>
  );
};

// Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white text-center p-8">
          <div>
            <h1 className="text-3xl font-serif mb-4">Montana Y Sol</h1>
            <p className="text-gray-400 mb-6">Ein Fehler ist aufgetreten. Bitte laden Sie die Seite neu.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 border border-white/20 rounded-full hover:bg-white/10 transition-colors"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main App Component
function App() {
  const [loading, setLoading] = useState(true);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <ConsentProvider>
            <div className="App">
              <AnimatePresence mode="wait">
                {loading && <LoadingScreen onComplete={() => setLoading(false)} />}
              </AnimatePresence>

              {!loading && (
                <>
                  <div className="noise-overlay" />
                  <Navigation />
                  <main>
                    <HeroSection />
                    <AboutSection />
                    <HighlightsSection />
                    <ImpressionenSection />
                    <ProjectDetailsSection />
                    {/* <TimelineSection /> */}
                    {/* <DroneStorySection /> */}
                    <LocationSection />
                    <ContactSection />
                  </main>
                  <Footer />
                  <WhatsAppButton />
                  <CookieBanner />
                </>
              )}
            </div>
          </ConsentProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
